import React, { useEffect, useRef, useState } from 'react'
import o from '../../components/Overlay.module.css'
import styles from '../TeamsPage.module.css'
import { useLayer } from '../../components/layers'
import { useAuth } from '../../contexts/AuthContext'
import { apiJson, ApiError } from '../../lib/api'
import { qrCardPdf, qrPdfName, type QrCard } from './qrCard'
import { schedulePdfName, teamSchedulePdf } from './schedule'
import type { LocationQrs } from '../pickingJobs/locationQrs'

// The clock-on QR dialog (V4 h 2646-2697): the square and its detail rows,
// then Download PDF. A rotating code has no square to print: a supervisor's
// phone shows it live. Otherwise the square comes from the API (a Crew QR v2,
// see qrCard.ts) and the download waits for it.
//
// Schedule picks the site's picking jobs the team works today and downloads
// the team's schedule: the clock-on card, then each job's location card
// (schedule.ts).

interface Props {
  teamId: string
  // The team's site, whose picking jobs Schedule offers; null when it has none.
  siteId: string | null
  // The card without its square: the dialog fetches that.
  card: QrCard
  // Set when a supervisor's phone shows the QR: the sentence that replaces
  // the square, and there is nothing to print.
  phoneNote: string | null
  printedOn: string
  onClose: () => void
}

interface Job {
  id: string
  name: string
  exact_variety: string
  active: boolean
  archived_at?: string | null
}

// One path for the whole square: a rectangle per run of dark modules in a row.
function qrPath(rows: string[]) {
  let d = ''
  rows.forEach((row, y) => {
    for (const m of row.matchAll(/1+/g)) d += `M${m.index} ${y}h${m[0].length}v1h-${m[0].length}z`
  })
  return d
}

function save(pdf: string, name: string) {
  const url = URL.createObjectURL(new Blob([pdf], { type: 'application/pdf' }))
  const a = document.createElement('a')
  a.href = url
  a.download = name
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 60000)
}

export function TeamQrDialog({ teamId, siteId, card: info, phoneNote, printedOn, onClose }: Props) {
  const panelEl = useRef<HTMLDivElement>(null)
  const { requestClose } = useLayer(true, onClose, { el: panelEl, modal: true })
  const { user } = useAuth()
  const userRef = useRef(user)
  userRef.current = user
  const [modules, setModules] = useState<string[]>([])
  const [failed, setFailed] = useState<string | null>(null)
  const [scheduling, setScheduling] = useState(false)
  const [jobs, setJobs] = useState<Job[] | null>(null)
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const [busy, setBusy] = useState(false)
  const [schedFailed, setSchedFailed] = useState<string | null>(null)

  useEffect(() => {
    if (phoneNote) return
    let live = true
    apiJson<{ modules: string[] }>(userRef.current, `/api/teams/${teamId}/clock-on-qr`)
      .then((r) => live && setModules(r.modules))
      .catch((e) => live && setFailed(e instanceof ApiError ? e.message : 'Could not make the QR. Try again.'))
    return () => {
      live = false
    }
  }, [teamId, phoneNote])

  // The site's live jobs, read once Schedule is first opened.
  useEffect(() => {
    if (!scheduling || jobs || !siteId) return
    let live = true
    apiJson<Job[]>(userRef.current, `/api/picking-jobs?siteId=${siteId}`)
      .then((r) => live && setJobs(r.filter((j) => j.active && !j.archived_at)))
      .catch((e) => live && setSchedFailed(e instanceof ApiError ? e.message : 'Could not load the picking jobs. Try again.'))
    return () => {
      live = false
    }
  }, [scheduling, jobs, siteId])

  const card: QrCard = { ...info, modules }
  const ready = modules.length > 0

  const download = () => save(qrCardPdf(card, printedOn), qrPdfName(card.teamName))

  const toggle = (id: string) =>
    setPicked((p) => {
      const n = new Set(p)
      if (n.has(id)) n.delete(id)
      else n.add(id)
      return n
    })
  const all = !!jobs?.length && jobs.every((j) => picked.has(j.id))

  const downloadSchedule = async () => {
    if (!jobs) return
    setSchedFailed(null)
    setBusy(true)
    try {
      const chosen = jobs.filter((j) => picked.has(j.id))
      const qrs = await Promise.all(chosen.map((j) => apiJson<LocationQrs>(userRef.current, `/api/picking-jobs/${j.id}/location-qrs`)))
      save(teamSchedulePdf(card, qrs, printedOn), schedulePdfName(card.teamName, new Date()))
    } catch (e) {
      setSchedFailed(e instanceof ApiError ? e.message : 'Could not make the schedule. Try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={o.scrim} onClick={(e) => e.target === e.currentTarget && requestClose()}>
      <div ref={panelEl} tabIndex={-1} className={`${o.modal} ${styles.qrModal}`} role="dialog" aria-label={`${card.kind}: ${card.teamName}`}>
        <div className={`${styles.qrHead} ${card.accent === 'receipt' ? styles.qrHeadReceipt : ''}`}>
          <div className={styles.qrHeadText}>
            <div className={styles.qrKind}>{scheduling ? 'Team schedule' : card.kind}</div>
            <h2 className={styles.qrTeam}>{card.teamName}</h2>
          </div>
          <button type="button" className={styles.qrClose} aria-label="Close" onClick={requestClose}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {scheduling ? (
          <div className={styles.schedStage}>
            <p className={styles.schedLead}>
              Tick the picking jobs this team works today. The schedule is the team’s clock-on QR, then a QR for each job that sets it on a picker’s phone.
            </p>
            {!siteId ? (
              <div className={styles.schedEmpty}>This team has no site, so there are no picking jobs to choose. Give it a site first.</div>
            ) : !jobs ? (
              <div className={styles.schedEmpty} role={schedFailed ? 'alert' : 'status'}>
                {schedFailed || 'Loading the picking jobs…'}
              </div>
            ) : !jobs.length ? (
              <div className={styles.schedEmpty}>This site has no active picking jobs. Add them in Picking jobs.</div>
            ) : (
              <>
                <label className={`${styles.schedJob} ${styles.schedAll}`}>
                  <input type="checkbox" checked={all} onChange={() => setPicked(all ? new Set() : new Set(jobs.map((j) => j.id)))} />
                  <span className={styles.schedName}>All jobs</span>
                  <span className={styles.schedVariety}>{jobs.length}</span>
                </label>
                <div className={styles.schedList}>
                  {jobs.map((j) => (
                    <label key={j.id} className={styles.schedJob}>
                      <input type="checkbox" checked={picked.has(j.id)} onChange={() => toggle(j.id)} />
                      <span className={styles.schedName}>{j.name}</span>
                      <span className={styles.schedVariety}>{j.exact_variety}</span>
                    </label>
                  ))}
                </div>
                {schedFailed && (
                  <div className={styles.schedError} role="alert">
                    {schedFailed}
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          <>
            <div className={styles.qrStage}>
              {phoneNote ? (
                <div className={styles.qrRotating}>
                  <span className={styles.qrPhone} aria-hidden="true">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="6" y="2" width="12" height="20" rx="3" />
                      <path d="M12 18h.01" />
                    </svg>
                  </span>
                  <div className={styles.qrRotTitle}>Shown on the supervisor’s phone</div>
                  <div className={styles.qrRotNote}>{phoneNote}</div>
                </div>
              ) : (
                <>
                  <div className={styles.qrFrame}>
                    {ready ? (
                      <svg className={styles.qrSquare} viewBox={`0 0 ${modules.length} ${modules.length}`} shapeRendering="crispEdges" role="img" aria-label={`Clock-on QR for ${card.teamName}`}>
                        <path d={qrPath(modules)} fill="#201e1d" />
                      </svg>
                    ) : (
                      <div className={styles.qrSquare} role={failed ? 'alert' : 'status'}>
                        <span className={styles.qrWait}>{failed || 'Making the QR…'}</span>
                      </div>
                    )}
                  </div>
                  <span className={styles.qrScanNote}>{info.note}</span>
                </>
              )}
            </div>

            <div className={styles.qrRows}>
              {card.rows.map((r) => (
                <div key={r.label} className={styles.qrRow}>
                  <span className={styles.qrRowLabel}>{r.label}</span>
                  <span className={styles.qrRowValue}>{r.value}</span>
                </div>
              ))}
            </div>
          </>
        )}

        <div className={styles.qrFoot}>
          {scheduling ? (
            <>
              <button type="button" className={o.ghost} onClick={() => setScheduling(false)}>
                Back
              </button>
              <button
                type="button"
                className={`${styles.qrPrint} ${styles.qrPdf}`}
                onClick={downloadSchedule}
                disabled={!ready || !picked.size || busy}
                title={picked.size ? undefined : 'Tick at least one picking job'}
              >
                {busy ? 'Making the schedule…' : 'Download schedule PDF'}
              </button>
            </>
          ) : (
            <>
              <button type="button" className={o.ghost} onClick={requestClose}>
                Close
              </button>
              {!phoneNote && (
                <>
                  <button type="button" className={`${o.ghost} ${styles.qrPdf}`} onClick={download} disabled={!ready}>
                    Download PDF
                  </button>
                  <button type="button" className={styles.qrPrint} onClick={() => setScheduling(true)} disabled={!ready} title="Pick today’s picking jobs and download the team’s schedule">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <rect x="3" y="4" width="18" height="18" rx="2" />
                      <path d="M16 2v4M8 2v4M3 10h18" />
                    </svg>
                    Schedule
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
